/*
  Opening and closing the desktop mega menu.

  Whether hover opens a panel is a theme setting; click always does. On hover it opens after a
  short delay so running the mouse along the bar does not flash every panel, and closing is
  delayed too so the diagonal move from a category down into its own panel does not lose it.
  Escape closes the panel and puts focus back on the category, and so does a click outside.

  The categories are links, not buttons, so Google follows them and a middle click still opens
  the collection in a tab; a plain click opens the panel instead of navigating, which is what
  the mockup does.
*/
if (!customElements.get('mega-menu')) {
  customElements.define(
    'mega-menu',
    class MegaMenu extends HTMLElement {
      constructor() {
        super();

        this.openOnHover = this.dataset.openOn !== 'click';
        this.openDelay = parseInt(this.dataset.openDelay, 10) || 90;
        this.closeDelay = parseInt(this.dataset.closeDelay, 10) || 200;
        this.openTimer = null;
        this.closeTimer = null;
        this.openPaneId = null;

        this.bar = this.querySelector('.mm-bar__inner');
        this.triggers = Array.from(this.querySelectorAll('[data-mm-trigger]'));
        this.panel = this.querySelector('[data-mm-panel]');
        this.dim = this.querySelector('[data-mm-dim]');

        this.onKeydown = this.onKeydown.bind(this);
        this.onFocusin = this.onFocusin.bind(this);
        this.onResize = this.fit.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
      }

      connectedCallback() {
        this.onDisable = this.teardown.bind(this);
        this.onEnable = this.init.bind(this);
        document.addEventListener('mega-menu:disabled', this.onDisable);
        document.addEventListener('mega-menu:enabled', this.onEnable);

        // The A/B test can have turned the menu off already; the switch in the section owns it.
        if (document.documentElement.dataset.megaMenu !== 'off') this.init();
      }

      /*
        Split in two so the test can switch the menu off and on again at any point. The
        listeners on the menu's own elements are bound once and left alone: the control group
        has the whole thing under display:none, where none of them can be reached. The ones on
        the document are the listeners that would reach past it, so those come and go.
      */
      init() {
        if (this.live) return;
        this.live = true;

        if (!this.bound) {
          this.bound = true;
          this.bindControls();
        }

        // The dim only covers the page below the bar, so a click anywhere else closes too.
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('focusin', this.onFocusin);

        this.fit();
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => this.fit());
          this.observer.observe(this.bar);
        } else {
          window.addEventListener('resize', this.onResize);
        }

        this.followHeader();
      }

      /*
        The bar sticks under the header (see component-mega-menu-redesign.css), so it needs the
        header's height as it is now, not as it was on load: "reduce logo size" shrinks it on
        scroll. And when the header hides on scroll down, an open panel goes with it.
      */
      followHeader() {
        const header = document.querySelector('.section-header');
        const section = this.closest('.mega-menu-section');
        if (!header) return;

        const root = document.documentElement.style;
        const measure = () => {
          root.setProperty('--mm-header-height', `${header.offsetHeight}px`);
          if (section) root.setProperty('--mm-bar-height', `${section.offsetHeight}px`);
        };

        measure();
        if ('ResizeObserver' in window) {
          this.headerObserver = new ResizeObserver(measure);
          this.headerObserver.observe(header);
          if (section) this.headerObserver.observe(section);
        }

        this.headerClassObserver = new MutationObserver(() => {
          if (this.openPaneId && header.classList.contains('shopify-section-header-hidden')) this.close();
        });
        this.headerClassObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
      }

      teardown() {
        if (!this.live) return;
        this.live = false;

        this.close();
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('focusin', this.onFocusin);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('resize', this.onResize);
        if (this.observer) this.observer.disconnect();
        if (this.headerObserver) this.headerObserver.disconnect();
        if (this.headerClassObserver) this.headerClassObserver.disconnect();
      }

      bindControls() {
        this.triggers.forEach((trigger, index) => {
          if (this.openOnHover) {
            trigger.addEventListener('mouseenter', () => {
              clearTimeout(this.closeTimer);
              clearTimeout(this.openTimer);
              // Moving between categories while a panel is up switches at once, with no delay.
              const delay = this.openPaneId ? 0 : this.openDelay;
              this.openTimer = setTimeout(() => this.open(trigger), delay);
            });

            trigger.addEventListener('mouseleave', () => {
              clearTimeout(this.openTimer);
              this.scheduleClose();
            });
          }

          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            if (this.openPaneId === trigger.dataset.mmTrigger) {
              this.close();
            } else {
              this.open(trigger);
            }
          });

          trigger.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
              event.preventDefault();
              const step = event.key === 'ArrowRight' ? 1 : -1;
              const next = this.triggers[(index + step + this.triggers.length) % this.triggers.length];
              next.focus();
              if (this.openPaneId) this.open(next);
              return;
            }

            if (event.key === 'ArrowDown') {
              event.preventDefault();
              this.open(trigger);
              const first = this.paneFor(trigger)?.querySelector('a');
              if (first) first.focus();
            }
          });
        });

        if (this.openOnHover) {
          // Leaving the bar sideways into the quick links is a close, not a switch.
          this.querySelectorAll('.mm-quick a').forEach((link) => {
            link.addEventListener('mouseenter', () => this.scheduleClose(60));
          });

          if (this.panel) {
            this.panel.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
            this.panel.addEventListener('mouseleave', () => this.scheduleClose());
          }

          if (this.dim) this.dim.addEventListener('mouseenter', () => this.scheduleClose(120));
        }

        if (this.dim) this.dim.addEventListener('click', () => this.close());
      }

      disconnectedCallback() {
        this.teardown();
        document.removeEventListener('mega-menu:disabled', this.onDisable);
        document.removeEventListener('mega-menu:enabled', this.onEnable);
      }

      /*
        How wide the bar needs to be depends on what the category titles say, and those are
        edited in the admin, so a breakpoint cannot know when the row stops fitting. Two things
        are dropped in turn until it does: the part after the comma in the right-hand links
        ("Clearance (Up to 85% Off)" becomes "Clearance"), then their icons, and if the menu has
        grown past the four categories of the design, every label is tightened up.
      */
      fit() {
        if (!this.bar) return;

        this.bar.classList.remove('mm-bar--tight', 'mm-bar--tighter', 'mm-bar--compact');
        if (!this.overflowing()) return;

        this.bar.classList.add('mm-bar--tight');
        if (!this.overflowing()) return;

        this.bar.classList.add('mm-bar--tighter');
        if (!this.overflowing()) return;

        // Last resort, for a menu with more categories than the four in the design.
        this.bar.classList.add('mm-bar--compact');
      }

      overflowing() {
        return this.bar.scrollWidth > this.bar.clientWidth;
      }

      paneFor(trigger) {
        return this.querySelector('#' + trigger.dataset.mmTrigger);
      }

      open(trigger) {
        clearTimeout(this.closeTimer);
        this.openPaneId = trigger.dataset.mmTrigger;

        this.triggers.forEach((item) => {
          item.setAttribute('aria-expanded', item === trigger ? 'true' : 'false');
        });

        this.querySelectorAll('.mm-pane').forEach((pane) => {
          pane.classList.toggle('is-active', pane.id === this.openPaneId);
        });

        this.classList.add('is-open');
      }

      close() {
        this.openPaneId = null;
        this.classList.remove('is-open');
        this.triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      }

      scheduleClose(delay) {
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.close(), delay === undefined ? this.closeDelay : delay);
      }

      onKeydown(event) {
        if (event.key !== 'Escape' || !this.openPaneId) return;
        const trigger = this.triggers.find((item) => item.dataset.mmTrigger === this.openPaneId);
        this.close();
        if (trigger) trigger.focus();
      }

      onDocumentClick(event) {
        if (this.openPaneId && !this.contains(event.target)) this.close();
      }

      onFocusin(event) {
        // Tabbing out of the menu closes it, so the panel never hangs over the page unattended.
        if (this.openPaneId && !this.contains(event.target)) this.close();
      }
    }
  );
}
